import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser, user } = useAuth();
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) return setState("error");
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          return setState("paid");
        }
        if (data.status === "expired" || data.payment_status === "failed") return setState("error");
      } catch {}
      if (++attempts < 8) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [sessionId, refreshUser]);

  return (
    <main className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="payment-success-page">
      {state === "checking" && <><span className="mx-auto block h-3 w-3 rounded-full bg-emerald-400 pulse-dot" /><p className="mt-6 text-slate-300" data-testid="payment-checking">Confirming your payment…</p></>}
      {state === "paid" && (
        <div className="glass-card glow-green p-10 fade-up">
          <CheckCircle2 size={48} className="mx-auto text-emerald-400" />
          <h1 className="font-display mt-4 text-4xl font-black uppercase tracking-tight text-white">You're in.</h1>
          <p className="mt-2 text-slate-400" data-testid="payment-paid">Your account is now on the <span className="font-bold uppercase text-emerald-400">{user?.tier}</span> plan.</p>
          <Link to="/dashboard" data-testid="payment-go-dashboard" className="btn-primary mt-8">Go to Dashboard</Link>
        </div>
      )}
      {(state === "error" || state === "timeout") && (
        <div className="glass-card p-10">
          <XCircle size={48} className="mx-auto text-amber-400" />
          <p className="mt-4 text-slate-300" data-testid="payment-pending">{state === "timeout" ? "Payment is still processing. Your plan will update shortly." : "We couldn't confirm this payment."}</p>
          <Link to="/pricing" className="btn-ghost mt-8">Back to pricing</Link>
        </div>
      )}
    </main>
  );
}

export function PaymentCancel() {
  return (
    <main className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="payment-cancel-page">
      <div className="glass-card p-10">
        <XCircle size={48} className="mx-auto text-slate-500" />
        <h1 className="font-display mt-4 text-3xl font-black uppercase tracking-tight text-white">Checkout cancelled</h1>
        <p className="mt-2 text-slate-400">No charge was made. Your free access continues.</p>
        <Link to="/pricing" className="btn-ghost mt-8">Back to pricing</Link>
      </div>
    </main>
  );
}
